import * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    isSiteSetupConversation,
    SITE_SETUP_MOVE_ON_CHOICES,
    SITE_SETUP_STEPS,
} from '@services/content-agents/browser';

import { useNavigation } from '../../navigation';
import { useCurrentOrganizationId } from '../../state';
import {
    agentIsProcessing,
    type AgentStateSource,
    type OrganizationAgentLocation,
    useAgentState,
} from '../Agent';
import { SiteSetupEnd } from './SiteSetupEnd';
import {
    selectCurrentSiteSetupStep,
    selectContinuingSiteSetupStep,
    selectSiteSetupEnd,
    selectUnansweredContinuingSiteSetupStep,
    selectUnansweredSiteSetupStep,
    SiteSetupStepsDeck,
    SiteSetupStepsProvider,
} from './SiteSetupStepCard';
import { useRecordSiteSetupStepStatuses } from './siteSetupStepStatuses';
import { FIRST_EDIT_STEP } from './steps/firstEditStep';
import { useGitSyncStep } from './steps/gitSyncStep';
import { usePublishStep } from './steps/publishStep';
import type { SiteSetupQuestion, SiteSetupStepBehavior } from './steps/types';

/** The setup step choice that skips the step, sent as the step's own skip message. */
const SKIP_SITE_SETUP_STEP_LABEL = 'Skip this step';

/**
 * What a site's setup conversation adds to its chat: the step deck at its top, the current step's
 * choices until the user answers, the step cards' context around it, and the end cards once the
 * site is published. Null for any other conversation.
 *
 * Each step's own behavior lives in `steps/`; this combines them.
 *
 * @param props.location - Where the conversation is shown, including its site, if any.
 * @param props.postSuggestion - Sends a message for the user, as the chat sends a suggestion.
 * @param props.onOpenNewConversation - Opens a new conversation, from the end cards.
 */
export function useSiteSetupChatSteps(props: {
    agent: AgentStateSource;
    conversationId: string;
    location: OrganizationAgentLocation;
    postSuggestion: (message: string) => void;
    onOpenNewConversation?: () => void;
}): {
    /** The step the conversation is on, if any. */
    step: api.SiteSetupStepId | null;
    header: React.ReactNode;
    /** The current step's choices, until the user answers them. */
    question: SiteSetupQuestion | undefined;
    /** Offered with a change request's own next steps while on the current step. */
    changeRequestNextSteps: string[];
    /** The end cards, once the agent has said its last words on the setup. */
    footer: React.ReactNode;
    chatSiteSetupStepsProvider: (chat: React.ReactNode) => React.ReactNode;
} | null {
    const { agent, conversationId, location, postSuggestion, onOpenNewConversation } = props;
    const { siteId } = location;
    const organizationId = useCurrentOrganizationId();
    const { navigate } = useNavigation();
    const setupSiteId =
        siteId !== undefined && isSiteSetupConversation(conversationId, siteId)
            ? siteId
            : undefined;

    const currentStep = useAgentState(
        agent,
        setupSiteId ? selectCurrentSiteSetupStep : selectNoStep
    );
    // A step just done, while the next one waits: the conversation is still on it.
    const continuingStep = useAgentState(
        agent,
        setupSiteId ? selectContinuingSiteSetupStep : selectNoStep
    );
    const step = currentStep ?? continuingStep;
    const unansweredStep = useAgentState(
        agent,
        setupSiteId ? selectUnansweredSiteSetupStep : selectNoStep
    );
    const unansweredContinuingStep = useAgentState(
        agent,
        setupSiteId ? selectUnansweredContinuingSiteSetupStep : selectNoStep
    );
    const ended = useAgentState(agent, setupSiteId ? selectSiteSetupEnd : selectNotEnded);
    const isProcessing = useAgentState(agent, agentIsProcessing);
    useRecordSiteSetupStepStatuses({ agent, siteId: setupSiteId });

    const publish = usePublishStep({
        agent,
        siteId: setupSiteId,
        isCurrent: step === api.SiteSetupStepId.Publish,
        postSuggestion,
    });
    const gitSync = useGitSyncStep({ siteId: setupSiteId, location });
    const behaviors: Partial<Record<api.SiteSetupStepId, SiteSetupStepBehavior>> = {
        [api.SiteSetupStepId.FirstEdit]: FIRST_EDIT_STEP,
        [api.SiteSetupStepId.GitSync]: gitSync,
        [api.SiteSetupStepId.Publish]: publish,
    };
    const stepActions = React.useMemo(
        () => ({ [api.SiteSetupStepId.Publish]: publish.cardAction }),
        [publish.cardAction]
    );

    if (!setupSiteId) {
        return null;
    }

    return {
        step,
        header: <SiteSetupStepsDeck />,
        question: unansweredStep
            ? getStepQuestion(unansweredStep, behaviors[unansweredStep], postSuggestion)
            : unansweredContinuingStep
              ? getMoveOnQuestion(unansweredContinuingStep, postSuggestion)
              : undefined,
        changeRequestNextSteps: (step && behaviors[step]?.changeRequestNextSteps) || [],
        footer:
            ended && !isProcessing && organizationId ? (
                <SiteSetupEnd
                    organizationId={organizationId}
                    siteId={setupSiteId}
                    onOpenConversation={() => onOpenNewConversation?.()}
                    onOpenSite={() =>
                        navigate({
                            screen: {
                                type: 'site',
                                organization: organizationId,
                                site: setupSiteId,
                            },
                        })
                    }
                />
            ) : undefined,
        chatSiteSetupStepsProvider: (chat) => (
            <SiteSetupStepsProvider
                agent={agent}
                onStepAction={postSuggestion}
                stepActions={stepActions}
            >
                {chat}
            </SiteSetupStepsProvider>
        ),
    };
}

/**
 * A step's cards: the ones a choice opened, or else its choices and a skip, as skeletons while the
 * step loads. A picked choice is left to its step, and otherwise sent: its label, or the step's
 * skip message for the skip.
 */
function getStepQuestion(
    step: api.SiteSetupStepId,
    behavior: SiteSetupStepBehavior | undefined,
    postSuggestion: (message: string) => void
): SiteSetupQuestion {
    if (behavior?.question) {
        return behavior.question;
    }

    const { action, choices } = SITE_SETUP_STEPS[step];
    return {
        options: [
            ...choices.map(({ label, description }) => ({ label, description })),
            ...(action.kind === 'skip'
                ? [{ label: SKIP_SITE_SETUP_STEP_LABEL, description: 'Come back to it any time.' }]
                : []),
        ],
        loading: behavior?.isLoading,
        onAnswer: (answer) => {
            const choice = choices.find((item) => item.label === answer);
            if (choice && behavior?.onChoice?.(choice)) {
                return;
            }
            postSuggestion(
                action.kind === 'skip' && answer === SKIP_SITE_SETUP_STEP_LABEL
                    ? action.message
                    : answer
            );
        },
    };
}

/** Once a step the user may keep working on is done: move on to the next step, or keep on with it. */
function getMoveOnQuestion(
    step: api.SiteSetupStepId,
    postSuggestion: (message: string) => void
): SiteSetupQuestion {
    const { next, keepEditing } = SITE_SETUP_MOVE_ON_CHOICES;
    return {
        options: [
            { label: next.label, description: next.description },
            { label: keepEditing.label, description: SITE_SETUP_STEPS[step].keepEditing },
        ],
        onAnswer: postSuggestion,
    };
}

/** No step, outside a setup conversation. Module-level so its identity stays stable. */
function selectNoStep(): null {
    return null;
}

/** No end, outside a setup conversation. Module-level so its identity stays stable. */
function selectNotEnded(): boolean {
    return false;
}
